import classNames from 'classnames'
import React, { PropsWithChildren } from 'react'

import { BaseComponentProps } from '@ui/common/component.props'
import { Loader } from '@ui/components/Spinner/Loader'
import { LoaderIcon } from '@ui/components/Spinner/Loader.icon'
import { IconRenderer, IconRendererType } from '@ui/icons/utils/IconRenderer'

import { Box } from '@shared/components/Box'

import styles from './Button.module.scss'

export interface ButtonProps
  extends BaseComponentProps<Omit<HTMLButtonElement, 'className' | 'style'>> {
  variant?: ButtonVariant
  size?: ButtonSize
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  textOverflow?: 'nowrap' | 'wrap' | 'truncate'
  tabularNums?: boolean
  iconLeft?: IconRendererType
  iconRight?: IconRendererType
  endDecorator?: React.ReactNode
  children?: string
  type?: 'submit' | 'reset' | 'button' | undefined
  ref?: React.Ref<HTMLButtonElement>
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onDisabledClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'brand'
  | 'ghost'
  | 'tertiary'
  | 'info'
  | 'success'
  | 'danger'
  | 'warning'
  | 'primary-new'

type ButtonVariantProps = Omit<ButtonProps, 'variant'>

const ButtonRoot: React.FC<PropsWithChildren<ButtonProps>> = (props) => {
  const {
    size = 'md',
    variant = 'primary',
    disabled = false,
    loading = false,
    fullWidth = false,
    textOverflow = 'nowrap',
    tabularNums = false,
    iconLeft,
    iconRight,
    endDecorator,
    type = 'button',
    onClick,
    onDisabledClick,
    children,
    ...restProps
  } = props

  const showLoaderOverlay = loading && !iconLeft && !iconRight
  const swapLeftToLoad = loading && iconLeft && !iconRight
  const swapRightToLoad = loading && iconRight

  const buttonClasses = classNames(
    styles.button,
    styles[`size-${size}`],
    styles[`variant-${variant}`],
    {
      [styles.stateDisabled]: disabled || loading,
      [styles.stateLoading]: showLoaderOverlay,
      [styles.fullWidth]: fullWidth,
      [styles.allowWrap]: textOverflow === 'wrap',
      [styles.truncate]: textOverflow === 'truncate',
      [styles.tabularNums]: tabularNums,
    }
  )

  const iconSize = getIconSize(size)

  const handleInteraction = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      event.preventDefault()

      if (disabled && onDisabledClick) onDisabledClick(event)
      return
    }

    if (onClick) onClick(event)
  }

  const IconLoaderSwapper = () => (
    <Box p='x2' align='center' justify='center' fullHeight>
      <IconRenderer icon={LoaderIcon} size={iconSize - 4} />
    </Box>
  )

  return (
    <button
      {...restProps}
      className={buttonClasses}
      aria-disabled={disabled || loading}
      onClick={handleInteraction}
      type={type}
      tabIndex={disabled || loading ? -1 : 0}
    >
      <Loading size={iconSize} loading={showLoaderOverlay}>
        {iconLeft && (
          <>
            {swapLeftToLoad ? (
              <IconLoaderSwapper />
            ) : (
              <span className={styles.iconLeft}>
                <IconRenderer icon={iconLeft} size={iconSize} />
              </span>
            )}
          </>
        )}
        {children && <span className={styles.text}>{children}</span>}
        {endDecorator && <span className={styles.iconRight}>{endDecorator}</span>}
        {iconRight && (
          <>
            {swapRightToLoad ? (
              <IconLoaderSwapper />
            ) : (
              <span className={styles.iconRight}>
                <IconRenderer icon={iconRight} size={iconSize} />
              </span>
            )}
          </>
        )}
      </Loading>
    </button>
  )
}

interface LoadingProps extends PropsWithChildren {
  size: number
  loading: boolean
}

const Loading: React.FC<LoadingProps> = (props) => {
  const { size, loading = false, children } = props

  return (
    <>
      {loading && (
        <div className={styles.loader}>
          <Loader size={size} color='currentColor' />
        </div>
      )}
      {children}
    </>
  )
}

const getIconSize = (size: ButtonSize): number => {
  return {
    xs: 14,
    sm: 16,
    md: 18,
    lg: 20,
    xl: 24,
  }[size]
}

export const Button = Object.assign(ButtonRoot, {
  Primary: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='primary' />
  ),
  Secondary: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='secondary' />
  ),
  Brand: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='brand' />
  ),
  Ghost: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='ghost' />
  ),
  Tertiary: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='tertiary' />
  ),
  Info: (props: PropsWithChildren<ButtonVariantProps>) => <ButtonRoot {...props} variant='info' />,
  Success: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='success' />
  ),
  Danger: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='danger' />
  ),
  Warning: (props: PropsWithChildren<ButtonVariantProps>) => (
    <ButtonRoot {...props} variant='warning' />
  ),
})
